/**
 * Feature Flags Configuration
 * 
 * Control which features are enabled/disabled across the application.
 * This allows for phased rollouts and easy feature toggling.
 */

export interface FeatureFlags {
  // Stock Management Features
  enableWarehouseManagement: boolean;
  enableBatchLotTracking: boolean;
  enableSerialNumberTracking: boolean;
  enableExpiryDateTracking: boolean;
  enableStockReservations: boolean;
  enableLocationCapacityLimits: boolean;
  
  // Transfer Features
  enableTransferApprovalWorkflow: boolean;
  enableScheduledTransfers: boolean;
  
  // Reporting Features
  enableAdvancedReporting: boolean;
  enableStockValuation: boolean;
}

/**
 * Default feature flags configuration
 * 
 * Phase 1: Basic stock management
 * Phase 2: Advanced tracking (batch/lot, serial, expiry)
 * Phase 3: Workflow and approvals
 */
export const featureFlags: FeatureFlags = {
  // Core Features - Enabled by default
  enableWarehouseManagement: true, // Enabled - can be disabled for single-location businesses
  
  // Phase 2 Features - Disabled for Phase 1
  enableBatchLotTracking: false,
  enableSerialNumberTracking: false,
  enableExpiryDateTracking: false,
  enableStockReservations: false,
  enableLocationCapacityLimits: false,
  
  // Phase 3 Features - Disabled for Phase 1
  enableTransferApprovalWorkflow: false,
  enableScheduledTransfers: false,
  
  // Reporting Features
  enableAdvancedReporting: false,
  enableStockValuation: true, // Enabled - basic valuation
};

/**
 * Helper function to get current feature flags
 * This reads from localStorage to get the current state
 */
export const getCurrentFeatureFlags = (): FeatureFlags => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('stock-feature-flags');
    if (stored) {
      try {
        const flags = JSON.parse(stored);
        return { ...featureFlags, ...flags };
      } catch (e) {
        console.error('Failed to parse stored feature flags:', e);
      }
    }
  }
  return featureFlags;
};

/**
 * Helper function to check if a feature is enabled
 * This reads from localStorage to get the current state
 */
export const isFeatureEnabled = (feature: keyof FeatureFlags): boolean => {
  const currentFlags = getCurrentFeatureFlags();
  return currentFlags[feature];
};

/**
 * Helper function to get all enabled features
 */
export const getEnabledFeatures = (): string[] => {
  return Object.entries(featureFlags)
    .filter(([_, enabled]) => enabled)
    .map(([feature]) => feature);
};

/**
 * Helper function to get all disabled features
 */
export const getDisabledFeatures = (): string[] => {
  return Object.entries(featureFlags)
    .filter(([_, enabled]) => !enabled)
    .map(([feature]) => feature);
};
